import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { FileBarChart } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { reportApi } from '../lib/report-api.js';
import { queryKeys } from '../lib/query-keys.js';
import { REPORT_CATEGORIES, type ReportSummary } from '../types/report.js';

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business ISO 日時文字列を YYYY/MM/DD 形式に整形する。
 * @graph-connects none
 */
function formatDate(iso: string): string {
  const [datePart] = iso.split('T');
  if (!datePart) return iso;
  const [y, m, d] = datePart.split('-');
  return y && m && d ? `${y}/${m}/${d}` : iso;
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business レポートカテゴリのバッジ。カテゴリ名に応じた色を返す。
 * @graph-connects none
 */
function CategoryBadge({ category }: { category: string }) {
  return (
    <span className="inline-flex shrink-0 items-center rounded-full bg-[var(--color-accent-primary)]/10 px-2 py-0.5 text-xs font-medium text-[var(--color-accent-primary)]">
      {category}
    </span>
  );
}

/**
 * @graph-node {Page}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business レポートストアの一覧ページ。蓄積されたスポット分析レポートをカテゴリフィルタ付きで新しい順に表示する。
 * @graph-connects reportApi [calls] レポート一覧 API
 * @graph-connects queryKeys [calls] react-query キーファクトリ
 */
export function ReportListPage() {
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.reports.list(selectedCategory || undefined),
    queryFn: () => reportApi.list(selectedCategory || undefined),
  });

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-10">
      <div className="mb-8">
        <div className="mb-1 font-mono text-xs tracking-widest text-[var(--color-accent-primary)] uppercase">
          Report Store
        </div>
        <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
          {t('reportList.title')}
        </h1>
        <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
          {t('reportList.description')}
        </p>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => {
            setSelectedCategory('');
          }}
          className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
            selectedCategory === ''
              ? 'bg-[var(--color-accent-primary)] text-white'
              : 'bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)]'
          }`}
        >
          {t('common.all')}
        </button>
        {REPORT_CATEGORIES.map((cat) => (
          <button
            key={cat}
            type="button"
            onClick={() => {
              setSelectedCategory(cat);
            }}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              selectedCategory === cat
                ? 'bg-[var(--color-accent-primary)] text-white'
                : 'bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)]'
            }`}
          >
            {cat}
          </button>
        ))}
      </div>

      {isLoading && (
        <div className="py-16 text-center text-sm text-[var(--color-text-muted)]">
          {t('app.loading')}
        </div>
      )}
      {error && (
        <Card className="border-[var(--color-error)]/20 p-6 text-center">
          <p className="text-sm text-[var(--color-error)]">{t('reportList.fetchError')}</p>
        </Card>
      )}
      {data && data.reports.length === 0 && (
        <div className="py-16 text-center">
          <FileBarChart
            className="mx-auto mb-3 h-10 w-10 text-[var(--color-text-muted)]"
            aria-hidden="true"
          />
          <p className="text-sm text-[var(--color-text-muted)]">
            {selectedCategory
              ? t('reportList.noDataInCategory', { category: selectedCategory })
              : t('reportList.noData')}
          </p>
        </div>
      )}
      {data && data.reports.length > 0 && (
        <ul className="flex flex-col gap-3">
          {data.reports.map((report: ReportSummary) => (
            <li key={report.id}>
              <Link
                to="/reports/$id"
                params={{ id: report.id }}
                className="block rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-4 transition-colors hover:border-[var(--color-accent-primary)]"
              >
                <div className="mb-1 flex items-center gap-2">
                  <CategoryBadge category={report.category} />
                  <span className="text-xs text-[var(--color-text-muted)]">
                    {formatDate(report.createdAt)}
                  </span>
                </div>
                <h2 className="text-base font-semibold text-[var(--color-text-primary)]">
                  {report.title}
                </h2>
                {report.summary && (
                  <p className="mt-1 line-clamp-2 text-sm text-[var(--color-text-secondary)]">
                    {report.summary}
                  </p>
                )}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
